import { forwardRef, useContext, useEffect, useMemo } from 'react';
import type { FunctionComponent, Ref, ElementType } from 'react';

import Flex from '../Flex';
import Image from '../Image';
import Button from '../Button';
import SearchInput from '../SearchInput';
import { useBreakpoint, useElement, useI18n, useTheme } from '../../hooks';
import type { ForwardProps } from '../../types';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { ThemeOverride } from '../Configuration';
import useThemeMode from '../../hooks/useThemeMode';
import MenuButton from '../MenuButton';

import type { AppHeaderProps } from './AppShell.types';
import AppShellOperator from './Operator';
import ContextSwitcher from './ContextSwitcher';
import {
  StyledAppHeader,
  StyledAppHeaderInfo,
  StyledAppHeaderSearchForm,
  StyledAppHeaderSpacer,
  StyledAppHeaderText,
  StyledAppHeaderOperator,
  StyledAppHeaderLogo
} from './AppHeader.styles';
import { StyledShareIcon } from './AppShell.styles';
import AppShellContext from './AppShellContext';
import { getHeaderTheme } from './style-utils';
import ThemeSwitcher from './ThemeSwitcher';

const AppHeader: FunctionComponent<AppHeaderProps & ForwardProps> = forwardRef(function AppHeader(
  {
    appName,
    contextSwitcher: { contexts, onContextClick, label: contextSwitcherLabel } = {},
    fullImageSrc,
    imageSrc,
    imageAltText,
    href,
    onClick,
    portalName,
    target,
    appNameHidden,
    operator,
    searchInput,
    searchLabel,
    searchPage,
    headerUtils,
    helpMenu,
    shareApp
  }: AppHeaderProps,
  ref: Ref<Element>
) {
  const t = useI18n();
  const isMediumOrAbove = useBreakpoint('md');
  const [searchContainer, setSearchContainer] = useElement<HTMLElement>();
  const { drawerOpen, setSearchContainerEl } = useContext(AppShellContext);
  const theme = useTheme();
  const headerTheme = useMemo(() => getHeaderTheme(theme), [theme]);

  const shareLabel = shareApp?.label ?? t('app_shell_share');

  useEffect(() => {
    if (searchPage && searchContainer) {
      setSearchContainerEl(searchContainer);
    } else {
      setSearchContainerEl(null);
    }
  }, [searchPage, searchContainer, setSearchContainerEl]);

  const { themeMode, onThemeModeChange } = useThemeMode();

  let appInfoAs: ElementType = 'div';
  if (href) appInfoAs = 'a';
  return (
    <ThemeOverride theme={headerTheme}>
      <Flex
        as={StyledAppHeader}
        container={{ alignItems: 'center', pad: 1 }}
        hideTitle={!isMediumOrAbove}
        drawerOpen={drawerOpen}
        ref={ref}
      >
        {contexts && contexts.length > 1 && onContextClick && (
          <ContextSwitcher
            contexts={contexts}
            onContextClick={onContextClick}
            label={contextSwitcherLabel}
          />
        )}

        <Flex as={StyledAppHeaderInfo} container={{ colGap: 0.5 }} centerLogo={!fullImageSrc}>
          <Flex
            container={{ inline: true, alignItems: 'center' }}
            as={appInfoAs}
            href={href}
            onClick={href ? onClick : undefined}
            target={target}
          >
            <Flex container={{ alignItems: 'center', gap: 1 }}>
              {(fullImageSrc || imageSrc) && (
                <Flex container={{ alignItems: 'center' }} as={StyledAppHeaderLogo}>
                  <Image
                    src={fullImageSrc || imageSrc}
                    alt={imageAltText ?? t('application_logo')}
                  />
                </Flex>
              )}
              <div>
                <StyledAppHeaderText
                  as={appNameHidden || !isMediumOrAbove ? VisuallyHiddenText : 'span'}
                  variant='primary'
                >
                  {appName}
                </StyledAppHeaderText>
                {portalName && (
                  <StyledAppHeaderText
                    as={!isMediumOrAbove ? VisuallyHiddenText : 'span'}
                    variant='secondary'
                  >
                    {portalName}
                  </StyledAppHeaderText>
                )}
              </div>
            </Flex>
          </Flex>
          {headerUtils?.[0]}
        </Flex>

        <Flex item={{ grow: 1 }} container={{ justify: 'end' }}>
          {searchInput ? (
            <StyledAppHeaderSearchForm
              ref={setSearchContainer}
              role='search'
              aria-label={searchLabel}
              onSubmit={e => e.preventDefault()}
              isMediumOrAbove={isMediumOrAbove}
            >
              {!searchPage && <SearchInput {...searchInput} />}
            </StyledAppHeaderSearchForm>
          ) : (
            <StyledAppHeaderSpacer />
          )}
          {headerUtils?.[1]}
        </Flex>
        {onThemeModeChange && (
          <ThemeSwitcher themeMode={themeMode} onThemeModeChange={onThemeModeChange} />
        )}
        {helpMenu && (
          <MenuButton
            variant='simple'
            icon='help'
            text={t('app_header_help')}
            iconOnly
            menu={{
              items: helpMenu
            }}
          />
        )}
        {shareApp && (
          <Button variant='secondary' compact onClick={shareApp.onClick}>
            <Flex container={{ gap: 0.5, alignItems: 'center' }}>
              <StyledShareIcon name='share' />
              <span>{shareLabel}</span>
            </Flex>
          </Button>
        )}
        <AppShellOperator
          as={StyledAppHeaderOperator}
          actions={operator.actions}
          popover={{ placement: 'bottom-start' }}
          name={operator.name}
        >
          {operator.avatar}
        </AppShellOperator>
      </Flex>
    </ThemeOverride>
  );
});

export default AppHeader;
